import { useState, useEffect } from 'react';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { ApiRoutes, AppRoutes } from 'src/lib/routes';
import pluralize from 'pluralize';
import { GetStaticProps } from 'next';
import OtherOptions from 'src/components/OtherOptions';
import WithDefaultJsonLd from 'src/components/WithDefaultJsonLd';
import { Blog, Collection, FilterableType, FilterRecord, FilterTab, Pagination as TPagination } from 'src/types';
import useFilter from 'src/hooks/useFilter';
import useQuery from 'src/hooks/useQuery';
import { getFilterParamsFromReqQuery, defaultValue as filterDefaultValue } from 'src/contexts/FilterProvider';
import Breadcrumb from 'src/components/Breadcrumb';
import BreadcrumbItem from 'src/components/BreadcrumbItem';
import Link from 'next/link';
import useSearchBarState from 'src/hooks/useSearchBarState';
import dayjs from 'dayjs';
import Filter from 'src/components/filter';
import classNames from 'classnames';
import CollectionHorizontalCard from 'src/components/CollectionHorizontalCard';
import BlogCardV2 from 'src/components/BlogCardV2';
import Loading from 'src/components/Loading';
import { isEmpty } from 'lodash-es';
import { useTranslations } from 'next-intl';
import { prepareTranslations } from 'src/lib/prepare_translations';
import useAddLocaleToUrl from 'src/hooks/useAddLocaleToUrl';
import Pagination from 'src/components/Pagination';
import { scrollTo } from 'src/helpers';
import useInitialData from 'src/hooks/useInitialData';

const Collections: React.FC<{
  params?: {
    [key: string]: string | number | string[] | number[] | null | (number | string | undefined)[];
  };
  record: FilterRecord;
  afterFetch?: (count: number) => void;
  show: boolean;
}> = ({ params, record, afterFetch, show }) => {
  const router = useRouter()
  const translation = useTranslations('pages/search')
  const { page: qPage } = router.query
  const [page, setPage] = useState<number | undefined>(undefined);
  const [collections, setCollections] = useState<Collection[]>([]);
  const { data, isLoading } = useQuery<{ data: Collection[]; pagination: TPagination }>(
    record.type === 'Fts' && page && (page <= 1 || page > 1 && show) ? ApiRoutes.apiCollectionsRoute(
      {},
      {
        per_page: 8,
        page: page,
        view: 'standard',
        record_id: record.id,
        record_type: record.type,
        record_name: record.name,
        prev_path: record.prevPath,
        ...(show ? params || { sort: 'recommended' } : {}),
      }
    ).toUrl() : null
  );
  const { data: fetchedCollections, pagination } = data || { data: collections };

  useEffect(() => {
    if (typeof pagination?.totalCount === 'number') {
      afterFetch && afterFetch(pagination.totalCount);
    }
  }, [pagination?.totalCount]);

  useEffect(() => {
    if (fetchedCollections) {
      setCollections(fetchedCollections);
    }
  }, [fetchedCollections && fetchedCollections.map((o) => o.id).join(','), page]);

  useEffect(() => {
    if (router.isReady) {
      setPage(qPage ? Number(qPage) : 1)
    }
  }, [qPage, router])

  return (
    <div className={classNames('container', { 'tw-hidden': !show })}>
      {collections && collections.length > 0 ? (
        <div className="row">
          {collections.map((collection) => (
            <div key={collection.id} className="col-md-6 tw-mb-6 md:tw-mb-9">
              <CollectionHorizontalCard
                title={collection.name}
                collection={collection}
                highlightText={record.type === 'Fts' ? String(record.id) : undefined}
              />
            </div>
          ))}
        </div>
      ) : null}

      {isLoading ? (
        <div className="container tw-py-16">
          <Loading className="tw-justify-center" />
        </div>
      ) : null}

      {pagination && pagination.totalPages > 1 ? (
        <div className="tw-mt-0 md:tw-mt-12 tw-text-center">
          <Pagination
            className="tw-mt-8 tw-justify-center"
            window={2}
            paginationData={pagination}
            onClick={(page) => {
              router.push(
                AppRoutes.searchRoute(
                  {},
                  { ...router.query, page: page }
                ).toPath()
              );
            }}
          />
        </div>
      ) : null}
    </div>
  );
};

const Blogs: React.FC<{
  params?: {
    [key: string]: string | number | string[] | number[] | null | (number | string | undefined)[];
  };
  record: FilterRecord
  afterFetch?: (count: number) => void;
  show: boolean;
}> = ({ params, record, afterFetch, show }) => {
  const router = useRouter()
  const translation = useTranslations('pages/search')
  const { page: qPage } = router.query
  const [page, setPage] = useState<number | undefined>(undefined);
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const { data, isLoading } = useQuery<{ data: Blog[]; pagination: TPagination }>(
    record.type === 'Fts' && page && (page <= 1 || page > 1 && show) ? ApiRoutes.apiBlogsRoute(
      {},
      {
        record_id: record.id,
        record_type: record.type,
        record_name: record.name,
        prev_path: record.prevPath,
        per_page: 12,
        page: page,
        ...(show ? params || { sort: 'recommended' } : {}),
      }
    ).toUrl() : null
  );
  const { data: fetchedBlogs, pagination } = data || { data: blogs };

  useEffect(() => {
    if (typeof pagination?.totalCount === 'number') {
      afterFetch && afterFetch(pagination.totalCount);
    }
  }, [pagination?.totalCount]);

  useEffect(() => {
    if (fetchedBlogs) {
      setBlogs(fetchedBlogs);
    }
  }, [fetchedBlogs && fetchedBlogs.map((o) => o.id).join(','), page]);

  useEffect(() => {
    if (router.isReady) {
      setPage(qPage ? Number(qPage) : 1)
    }
  }, [qPage, router])

  return (
    <div className={classNames('container', { 'tw-hidden': !show })}>
      {blogs && blogs.length > 0 ? (
        <div className="row">
          {blogs.map((blog) => (
            <div key={blog.id} className="col-md-4 tw-mb-8 md:tw-mb-0">
              <BlogCardV2
                blog={blog}
                highlightText={record.type === 'Fts' ? String(record.id) : undefined}
              />
            </div>
          ))}
        </div>
      ) : null}

      {isLoading ? (
        <div className="container tw-py-16">
          <Loading className="tw-justify-center" />
        </div>
      ) : null}

      {pagination && pagination.totalPages > 1 ? (
        <div className="tw-mt-0 md:tw-mt-12 tw-text-center">
          <Pagination
            className="tw-mt-8 tw-justify-center"
            window={2}
            paginationData={pagination}
            onClick={(page) => {
              router.push(
                AppRoutes.searchRoute(
                  {},
                  { ...router.query, page: page }
                ).toPath()
              );
            }}
          />
        </div>
      ) : null}
    </div>
  );
};

type Tab = 'option' | 'collection' | 'blog'
const Page: React.FC = () => {
  const [params, setParams] = useState<Record<string, any>>()
  const [queryParams, setQueryParams] = useState<Record<string, any>>({})
  const [rawRecord, setRawRecord] = useState<FilterRecord>()
  const { giftCollections } = useInitialData()
  const [searchParams] = useSearchBarState();
  const addLocaleToUrl = useAddLocaleToUrl()
  const translation = useTranslations('pages/search')
  const { setFilterRecord } = useFilter();
  const [totalCount, setTotalCount] = useState<{ [key: string]: number | undefined }>({
    option: undefined,
    blog: undefined,
    collection: undefined,
  });
  const router = useRouter();
  const [currentTab, setCurrentTab] = useState<Tab>('option');
  const currentTabTotalCount = totalCount[currentTab];
  const allTabsTotalCount = Object.values(totalCount).some((v) => typeof v === 'undefined')
    ? undefined
    : (Object.values(totalCount) as number[]).reduce((memo: number, v) => memo + v, 0);

  useEffect(() => {
    if (router.isReady) {
       const { record_id, record_type = 'Fts', record_name, prev_path = AppRoutes.rootRoute({}).toPath(), tab, ...queryParams } = router.query;
       setQueryParams(queryParams)
       setCurrentTab(tab ? String(tab) as Tab : 'option')
       setRawRecord({
         id: record_id ? String(record_id) : '',
         name: record_name ? String(record_name) : 'Home',
         type: record_type as FilterableType,
         prevPath: String(prev_path)
       })
       setParams(
         (record_id && record_type) || record_type === 'Fts'
           ? {
               ...getFilterParamsFromReqQuery(queryParams),
               ...(record_type === 'Fts' &&
               (!queryParams['sort_by'] || queryParams['sort_by'] === filterDefaultValue['sortBy'])
                 ? { sortBy: 'rank' }
                 : {}),
             }
           : {}
       )
    }
  }, [router])

  useEffect(() => {
    if (rawRecord) {
      setFilterRecord(rawRecord);
    }
  }, [rawRecord]);

  useEffect(() => {
    if (Object.values(totalCount).every((v) => typeof v === 'number') && currentTab && currentTabTotalCount === 0) {
      const newTab = Object.keys(totalCount).filter((t) => {
        const count = totalCount[t]
        return t !== currentTab && count && count > 0
      })[0]
      if (newTab) {
        setCurrentTab(newTab as FilterTab)
      }
    }
  }, [currentTab, currentTabTotalCount, totalCount])

  return (
    <>
      <NextSeo
        title={translation('metaTitle')}
        description={translation('metaDescription')}
        canonical={addLocaleToUrl(AppRoutes.searchRoute({}).toUrl())}
        openGraph={{
          title: translation('metaTitle'),
          description: translation('metaDescription'),
          url: addLocaleToUrl(AppRoutes.searchRoute({}).toUrl()),
        }}
      />
      <WithDefaultJsonLd />

      <div className="container tw-mt-8 md:tw-mt-12">
        <Breadcrumb className="breadcrumb--black tw-justify-center md:tw-justify-start tw-mb-8 md:tw-mb-12">
          {rawRecord ? rawRecord.type === 'Country' ? (
            <BreadcrumbItem>
              <a href={decodeURIComponent(rawRecord.prevPath)}>
                {rawRecord.name}
              </a>
            </BreadcrumbItem>
          ) : (
            <BreadcrumbItem>
              <Link
                legacyBehavior prefetch={false}
                href={decodeURIComponent(rawRecord.prevPath)}
              >
                <a>{rawRecord.name}</a>
              </Link>
            </BreadcrumbItem>
          ) : null}
        </Breadcrumb>

        {typeof allTabsTotalCount === 'number' && allTabsTotalCount > 0 ? (
          <>
            {typeof currentTabTotalCount === 'number' ? (
              <>
                <h2 className="tw-font-semibold tw-text-xl md:tw-text-2xl tw-mb-0 tw-font-styleSans">
                  {rawRecord && rawRecord.type === 'Fts'
                    ? `${pluralize(
                        currentTab === 'option'
                          ? translation('experience')
                          : currentTab === 'blog'
                          ? translation('insiderTip')
                          : translation('collection'),
                        currentTabTotalCount,
                        true
                      )} ${rawRecord.id ? `${translation('for')} "${rawRecord.id}"` : `${translation('found')}`}`
                    : `${translation('showingResults')}${rawRecord ? ` ${translation('for')} "${rawRecord.name}"` : ''}`}
                </h2>
                <div className="tw-flex">
                  {searchParams.dates.length > 0 ? (
                    <>
                      <div className="tw-text-grey tw-text-sm">
                        {searchParams.dates.map((d) => dayjs(d).format('MMM DD')).join(' - ')}
                      </div>
                    </>
                  ) : null}

                  {searchParams.dates.length > 0 && searchParams.numGuest && searchParams.numGuest > 0 ? (
                    <div className="tw-mx-2 md:tw-mx-4">|</div>
                  ): null}

                  {searchParams.numGuest && searchParams.numGuest > 0 ? (
                    <>
                      <div className="tw-text-grey tw-text-sm">
                        {translation('pluralGuest', {count: searchParams.numGuest})}
                      </div>
                    </>
                  ) : null}
                </div>
              </>
            ) : null}

            {rawRecord && rawRecord.type === 'Fts' ? (
              <div className="list-horizontal-scroll list-horizontal-scroll--tab tw-mt-12">
                {totalCount['option'] && totalCount['option'] > 0 ? (
                  <div className="list-horizontal-scroll__item">
                    <div
                      className={classNames('list-horizontal-scroll__item-text', {
                        active: currentTab === 'option',
                      })}
                      onClick={() => {
                        if (currentTab === 'option') return;
                        router.push(
                          AppRoutes.searchRoute(
                            {},
                            { record_id: rawRecord.id, record_type: rawRecord.type, tab: 'option' }
                          ).toPath()
                        );
                      }}
                    >
                      {translation('experiences')}
                    </div>
                  </div>
                ) : null}
                {totalCount['collection'] && totalCount['collection'] > 0 ? (
                  <div className="list-horizontal-scroll__item">
                    <div
                      className={classNames('list-horizontal-scroll__item-text', {
                        active: currentTab === 'collection',
                      })}
                      onClick={() => {
                        if (currentTab === 'collection') return;
                        router.push(
                          AppRoutes.searchRoute(
                            {},
                            { record_id: rawRecord.id, record_type: rawRecord.type, tab: 'collection' }
                          ).toPath()
                        );
                      }}
                    >
                      {translation('collections')}
                    </div>
                  </div>
                ) : null}
                {totalCount['blog'] && totalCount['blog'] > 0 ? (
                  <div className="list-horizontal-scroll__item">
                    <div
                      className={classNames('list-horizontal-scroll__item-text', {
                        active: currentTab === 'blog',
                      })}
                      onClick={() => {
                        if (currentTab === 'blog') return;
                        router.push(
                          AppRoutes.searchRoute(
                            {},
                            { record_id: rawRecord.id, record_type: rawRecord.type, tab: 'blog' }
                          ).toPath()
                        );
                      }}
                    >
                      {translation('insiderTip')}
                    </div>
                  </div>
                ) : null}
                <div className="list-horizontal-scroll__item tw-w-full">
                  <div className="list-horizontal-scroll__item-text">&nbsp;</div>
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      {(typeof currentTabTotalCount === 'number' && currentTabTotalCount > 0) || !isEmpty(queryParams) ? (
        <div className="tw-mb-4">
          <Filter queryParams={params} contentClass="tw-bg-white" notSticky={true} tab={currentTab as FilterTab} />
        </div>
      ) : null}

      {rawRecord ? (
        <>
          <OtherOptions
            usePagination={true}
            hideTitle={true}
            record={rawRecord}
            queryParams={params}
            shouldScrollTop={true}
            useHtmlLink={true}
            afterFetch={(count) => {
              setTotalCount((prev) => ({ ...prev, option: count }));
            }}
            show={currentTab === 'option'}
            isGiftExperience={rawRecord.type === 'Collection' && !!giftCollections.find(o => String(o.id) === String(rawRecord.id))}
          />
          <Collections
            params={params}
            record={rawRecord}
            afterFetch={(count) => {
              setTotalCount((prev) => ({ ...prev, collection: count }));
            }}
            show={currentTab === 'collection'}
          />
          <Blogs
            params={params}
            record={rawRecord}
            afterFetch={(count) => {
              setTotalCount((prev) => ({ ...prev, blog: count }));
            }}
            show={currentTab === 'blog'}
          />
        </>
      ) : null}

      {typeof currentTabTotalCount === 'number' && currentTabTotalCount === 0 ? (
        <div className="container tw-mt-8">
          <div className="tw-font-normal tw-text-xl tw-mb-0 tw-leading-none md:tw-leading-none">
            {translation('searchGremlinsGotYourResults')}
          </div>
        </div>
      ) : null}

      <div className="tw-pb-8 md:tw-pb-12"></div>
    </>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const translations = await prepareTranslations(context, ['pages/search']);

  return { props: translations }
};

export default Page;
